import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  BalanceSplitResponse,
  CompanySummaryResponse,
  ExpenseBreakdownResponse,
  TimeSeriesResponse,
} from '@accounting/shared';
import { formatCurrency } from '../../lib/formatters';

function compactCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

function monthLabel(m: string): string {
  const [y, mm] = m.split('-').map(Number);
  return new Date(y!, mm! - 1, 1).toLocaleDateString('en-US', {
    month: 'short',
    year: '2-digit',
  });
}

type Row = { month: string } & Record<string, number | string>;

function toRows(ts: TimeSeriesResponse): Row[] {
  return ts.months.map((m, i) => {
    const row: Row = { month: monthLabel(m) };
    for (const s of ts.series) row[s.companyId] = s.values[i] ?? 0;
    return row;
  });
}

const tooltipFormatter = (v: number | string) => formatCurrency(String(v), true);

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <h2 style={{ fontSize: 15, margin: '0 0 12px' }}>{title}</h2>
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          {children as React.ReactElement}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function RevenueTrendChart({ data }: { data: TimeSeriesResponse }) {
  const rows = toRows(data);
  return (
    <ChartCard title="Revenue by month">
      <BarChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={compactCurrency} tick={{ fontSize: 12 }} width={56} />
        <Tooltip formatter={tooltipFormatter} />
        <Legend />
        {data.series.map((s) => (
          <Bar key={s.companyId} dataKey={s.companyId} name={s.companyName} stackId="rev" fill={s.color} />
        ))}
      </BarChart>
    </ChartCard>
  );
}

export function NetIncomeTrendChart({ data }: { data: TimeSeriesResponse }) {
  const rows = toRows(data);
  return (
    <ChartCard title="Net income by month">
      <LineChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={compactCurrency} tick={{ fontSize: 12 }} width={56} />
        <Tooltip formatter={tooltipFormatter} />
        <Legend />
        <ReferenceLine y={0} stroke="var(--text-muted)" />
        {data.series.map((s) => (
          <Line
            key={s.companyId}
            type="monotone"
            dataKey={s.companyId}
            name={s.companyName}
            stroke={s.color}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ChartCard>
  );
}

export function CashPositionChart({ data }: { data: TimeSeriesResponse }) {
  const rows = toRows(data);
  return (
    <ChartCard title="Cash position by month">
      <AreaChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={compactCurrency} tick={{ fontSize: 12 }} width={56} />
        <Tooltip formatter={tooltipFormatter} />
        <Legend />
        {data.series.map((s) => (
          <Area
            key={s.companyId}
            type="monotone"
            dataKey={s.companyId}
            name={s.companyName}
            stackId="cash"
            stroke={s.color}
            fill={s.color}
            fillOpacity={0.35}
          />
        ))}
      </AreaChart>
    </ChartCard>
  );
}

export function ExpenseBreakdownChart({ data }: { data: ExpenseBreakdownResponse }) {
  const palette = '#C23B22';
  return (
    <ChartCard title="Expense breakdown">
      <BarChart
        data={data.items}
        layout="vertical"
        margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis type="number" tickFormatter={compactCurrency} tick={{ fontSize: 12 }} />
        <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11 }} />
        <Tooltip formatter={tooltipFormatter} />
        <Bar dataKey="amount" name="Expense" fill={palette}>
          {data.items.map((item) => (
            <Cell key={item.key} fill={item.key === '__other__' ? '#8C6D1F' : palette} />
          ))}
        </Bar>
      </BarChart>
    </ChartCard>
  );
}

export function CompanySummaryTable({ data }: { data: CompanySummaryResponse }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <h2 style={{ fontSize: 15, margin: '0 0 12px' }}>Company summary</h2>
      <table className="table">
        <thead>
          <tr>
            <th>Company</th>
            <th style={{ textAlign: 'right' }}>Revenue</th>
            <th style={{ textAlign: 'right' }}>Expenses</th>
            <th style={{ textAlign: 'right' }}>Net Income</th>
            <th style={{ textAlign: 'right' }}>Cash</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.companyId}>
              <td>
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: r.color,
                    marginRight: 8,
                  }}
                />
                {r.companyName}
              </td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(r.revenue)}</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(r.expenses)}</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(r.netIncome, true)}</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(r.cash, true)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
            <td>Total</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(data.totals.revenue)}</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(data.totals.expenses)}</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(data.totals.netIncome, true)}</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(data.totals.cash, true)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * Where the group holds its assets, and who owes what — one slice per company.
 *
 * A pie is the right shape here only because the parts genuinely sum to a
 * meaningful whole: total assets held across the group. It answers "where is it
 * sitting", which a trend line cannot.
 *
 * ⚠️ Intercompany balances are NOT eliminated. A loan between two of the
 * companies shows as an asset in one and a liability in the other, exactly as
 * each company's own balance sheet states it. This is a location view, not a
 * consolidated balance sheet.
 */
export function BalanceSplitCharts({ data }: { data: BalanceSplitResponse }) {
  const pie = (
    title: string,
    side: BalanceSplitResponse['assets'],
    emptyNote: string,
  ) => (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <h2 style={{ fontSize: 15, margin: '0 0 4px' }}>{title}</h2>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{formatCurrency(side.total)}</span>
      </div>
      {side.slices.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: '12px 0 0' }}>{emptyNote}</p>
      ) : (
        <>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={side.slices}
                  dataKey="amount"
                  nameKey="companyName"
                  innerRadius={50}
                  outerRadius={92}
                  paddingAngle={1}
                >
                  {side.slices.map((s) => (
                    <Cell key={s.companyId} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip formatter={tooltipFormatter} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 0, listStyle: 'none', fontSize: 12 }}>
            {side.slices.map((s) => (
              <li key={s.companyId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block', width: 9, height: 9, borderRadius: 2,
                      background: s.color, marginRight: 6,
                    }}
                  />
                  {s.companyName}
                </span>
                <span className="muted">
                  {formatCurrency(s.amount)} · {s.percent.toFixed(1)}%
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {/* A company with nothing, or with a negative balance, cannot be a slice.
          Naming it beats leaving it silently out of the total. */}
      {side.excluded.length > 0 && (
        <p className="muted" style={{ fontSize: 11, margin: '8px 0 0' }}>
          Not shown:{' '}
          {side.excluded
            .map((e) => `${e.companyName} (${formatCurrency(e.amount)})`)
            .join(', ')}
        </p>
      )}
    </div>
  );

  return (
    <>
      {pie('Assets by company', data.assets, 'No company holds a positive asset balance.')}
      {pie('Liabilities by company', data.liabilities, 'No company carries a liability balance.')}
    </>
  );
}
